import React, { useState } from 'react';
import { Typography, Button, Container } from '@mui/material';
import { Link } from 'react-router-dom';
import FeatureShowcase from '../shared-components/FeatureShowcase';
import CTASection from '../shared-components/CTASection';
import TrustBadges from '../shared-components/TrustBadges';
import EHRComparison from '../shared-components/EHRComparison';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

function Providers() {
  const [videoOpen, setVideoOpen] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState('YOUR_VIDEO_ID');
  const [imageOpen, setImageOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const openVideo = (videoId) => {
    setCurrentVideoId(videoId);
    setVideoOpen(true);
  };

  const openImage = (imageSrc) => {
    setCurrentImage(imageSrc);
    setImageOpen(true);
  };

  // Operations Features
  const operationsFeatures = [
    {
      title: "AI Agent & Automation",
      description: "Your Virtual Colleague that executes complex clinical tasks. Whether using natural language or our secure email relay, Agentic AI drafts documentation and manages workflows with strict 'Human-in-the-Loop' confirmation to ensure clinical accuracy and privacy.",
      stat: "80% reduction in admin time",
      imagePosition: "left",
      imageFile: "assets/images/marketing/home/therapist-ai-chat-overlay.jpg",
      linkLabel: "Explore AI Agent",
      linkPath: "/features/ai-agent"
    },
    {
      title: "Client Management & Records",
      description: "Complete clinical documentation with pre-built templates plus unlimited custom forms. Version control tracks every change with full audit trail and revert capability.",
      stat: "Unlimited clinical templates",
      imagePosition: "right",
      imageFile: "assets/images/marketing/providers/client-list-laptop.jpg",
      linkLabel: "Explore Client Management",
      linkPath: "/features/client-management"
    },
    {
      title: "Scheduling & Public Booking",
      description: "24/7 digital front door with real-time availability checks. Rate limiting protects your schedule while automated verification ensures legitimate bookings. Syncs with Google, Outlook, and Apple calendars.",
      stat: "Streamlined for providers and clients",
      imagePosition: "left",
      imageFile: "assets/images/marketing/providers/provider-with-calendar-screenshot.jpg",
      linkLabel: "Explore Scheduling",
      linkPath: "/features/scheduling"
    },
    {
      title: "Billing & Payments",
      description: "One-click revenue cycle powered by Snapshot technology, capturing an immutable, audit-proof record at the moment of creation. Features integrated Stripe processing for automated, privacy-shielded invoicing.",
      stat: "< 1 minute to bill & pay",
      imagePosition: "right",
      imageFile: "assets/images/marketing/providers/provider-client-with-sessions.jpg",
      linkLabel: "Explore Billing",
      linkPath: "/features/billing"
    }
  ];

  // Network Features
  const networkFeatures = [
    {
      title: "Referral Exchange",
      description: "An intelligent marketplace designed to solve the Access Gap. Exchange care transitions using 20+ criteria, including precise cultural matching for provider gender, race, and language to ensure optimal therapeutic fit.",
      stat: "Improved patient/provider fit",
      imagePosition: "left",
      imageFile: "assets/images/marketing/providers/group-with-referral-detail.jpg",
      linkLabel: "Explore Referrals",
      linkPath: "/features/referrals"
    },
    {
      title: "Professional Profiles",
      description: "SEO-optimized public presence with 20+ healthcare-specific fields. Showcase credentials, specializations, and insurance accepted. Get discovered by patients searching for care and peers looking to collaborate.",
      stat: "Get discovered by patients",
      imagePosition: "right",
      imageFile: "assets/images/marketing/providers/laptop-with-profile-screenshot.jpg",
      linkLabel: "Explore Profiles",
      linkPath: "/features/profiles"
    },
    {
      title: "Social & Community",
      description: "Professional networking with discussion groups, peer connections, and content creation. Share articles, engage in conversations, and build your professional community.",
      stat: "Connect with peers nationwide",
      imagePosition: "left",
      imageFile: "assets/images/marketing/providers/home-with-article.jpg",
      linkLabel: "Explore Community",
      linkPath: "/features/social-community"
    }
  ];

  // Care Features
  const careFeatures = [
    {
      title: "Team Communication",
      description: "Real-time care coordination with secure messaging, video calls, and file sharing. Create dedicated channels for each care team with client-linked conversations and shared documentation.",
      stat: "HIPAA-compliant collaboration",
      imagePosition: "left",
      imageFile: "assets/images/marketing/providers/group-2-with-team-overlay.jpg",
      linkLabel: "Explore Teams",
      linkPath: "/features/teams"
    },
    {
      title: "Provider Collaboration",
      description: "Seamless external integration enabling providers to share a synchronized vision. Invite external colleagues to collaborate through reciprocal client records, secure authorization workflows, and unified communication.",
      stat: "Seamless multi-provider care",
      imagePosition: "right",
      imageFile: "assets/images/marketing/providers/provider-client-roi-screenshot.jpg",
      linkLabel: "Explore Collaboration",
      linkPath: "/features/provider-collaboration"
    },
    {
      title: "Organizations & Group Practices",
      description: "Manage group practices with centralized client records, programs, and locations. Define specific offerings like IOP/PHP programs. Transfer clients between providers and maintain unified oversight.",
      stat: "Showcase groups and programs",
      imagePosition: "left",
      imageFile: "assets/images/marketing/providers/org-profile.jpg",
      linkLabel: "Explore Organizations",
      linkPath: "/features/organizations"
    }
  ];

  return (
      <div className="min-h-screen">
        {/* HERO SECTION */}
        <section className="py-20 lg:py-32 bg-gradient-to-br from-blue-50 via-white to-white">
          <Container maxWidth="lg">
            <div className="text-center max-w-4xl mx-auto">
              <Typography
                  variant="h1"
                  align="center"
                  className="text-4xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight"
              >
                The All-in-One Platform for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Mental Health Professionals
              </span>
              </Typography>

              <Typography
                  variant="h2"
                  align="center"
                  className="text-lg lg:text-xl text-gray-600 mb-12 font-normal leading-relaxed"
              >
                Our integrated EHR and automated billing handle the operational heavy lifting, while actual care is enhanced with our advanced case management and collaboration tools.
              </Typography>

              <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
                <Button
                    component={Link}
                    to="/provider-sign-up"
                    variant="contained"
                    size="large"
                    className="bg-blue-600 hover:bg-blue-700 px-8 py-3.5 text-base font-bold shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5"
                >
                  Get Started Free
                </Button>
                <Button
                    component={Link}
                    to="/pricing"
                    variant="outlined"
                    size="large"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 px-8 py-3.5 text-base font-semibold"
                >
                  View Pricing
                </Button>
              </div>

              <div className="flex items-center justify-center gap-6 opacity-80">
                <TrustBadges />
              </div>
            </div>
          </Container>
        </section>

        {/* EHR COMPARISON SECTION */}
        <section className="py-24 bg-white shadow-inner">
          <Container maxWidth="lg" className="pb-16">
            <div className="text-center mb-12">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                What Is <span className="text-blue-600">Halespring</span>?
              </Typography>
              <Typography variant="body1" align="center" className="text-xl text-gray-600">
                A complete EHR with everything you need, plus unique features that set us apart.
              </Typography>
            </div>
            <EHRComparison />
          </Container>
        </section>

        {/* PILLAR 1: OPERATIONS */}
        <section className="py-24 bg-gray-50 shadow-inner">
          <Container maxWidth="lg">
            <div className="text-center mb-20 w-full max-w-3xl mx-auto">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                The Engine: <span className="text-blue-600">Practice Operations</span>
              </Typography>
              <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
                Intelligent automation that handles the busy work so you can focus on clinical care.
              </Typography>
            </div>

            <div className="space-y-36">
              {operationsFeatures.map((feature, index) => (
                  <FeatureShowcase
                      key={index}
                      title={feature.title}
                      description={feature.description}
                      stat={feature.stat}
                      imagePosition={feature.imagePosition}
                      imageFile={feature.imageFile}
                      linkLabel={feature.linkLabel}
                      linkPath={feature.linkPath}
                      onImageClick={openImage}
                  />
              ))}
            </div>
          </Container>
        </section>

        {/* PILLAR 2: NETWORK */}
        <section className="py-24 bg-white">
          <Container maxWidth="lg">
            <div className="text-center mb-20 w-full max-w-3xl mx-auto">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                The Network: <span className="text-blue-600">Professional Growth</span>
              </Typography>
              <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
                Build your practice through referrals, visibility, and professional connections.
              </Typography>
            </div>

            <div className="space-y-36">
              {networkFeatures.map((feature, index) => (
                  <FeatureShowcase
                      key={index}
                      title={feature.title}
                      description={feature.description}
                      stat={feature.stat}
                      imagePosition={feature.imagePosition}
                      imageFile={feature.imageFile}
                      linkLabel={feature.linkLabel}
                      linkPath={feature.linkPath}
                      onImageClick={openImage}
                  />
              ))}
            </div>
          </Container>
        </section>

        {/* PILLAR 3: CARE */}
        <section className="py-24 bg-gray-50">
          <Container maxWidth="lg">
            <div className="text-center mb-20 w-full max-w-3xl mx-auto">
              <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                The Connection: <span className="text-blue-600">Collaborative Care</span>
              </Typography>
              <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
                Coordinate with care teams, external providers, and group practices for better outcomes.
              </Typography>
            </div>

            <div className="space-y-36">
              {careFeatures.map((feature, index) => (
                  <FeatureShowcase
                      key={index}
                      title={feature.title}
                      description={feature.description}
                      stat={feature.stat}
                      imagePosition={feature.imagePosition}
                      imageFile={feature.imageFile}
                      linkLabel={feature.linkLabel}
                      linkPath={feature.linkPath}
                      onImageClick={openImage}
                  />
              ))}
            </div>
          </Container>
        </section>

        {/* FINAL CTA SECTION */}
        <CTASection
            headline="Ready to Transform Your Practice?"
            copy="Join thousands of mental health professionals using Halespring to reduce admin time and improve patient outcomes."
            primaryCTA="Get Started Free"
            primaryCTALink="/provider-sign-up"
            secondaryCTA="View Pricing"
            trustElements={[
              "Free account - no card needed",
              "Premium trial available",
              "HIPAA BAA included",
              "Cancel anytime"
            ]}
        />

        {/* Video Lightbox */}
        <Lightbox
            open={videoOpen}
            close={() => setVideoOpen(false)}
            slides={[
              {
                type: 'custom',
                children: (
                    <div className="w-full h-full flex items-center justify-center">
                      <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${currentVideoId}?autoplay=1`}
                          title="Demo Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ maxWidth: '1200px', maxHeight: '675px', aspectRatio: '16/9' }}
                      />
                    </div>
                )
              }
            ]}
        />

        {/* Image Lightbox */}
        <Lightbox
            open={imageOpen}
            close={() => setImageOpen(false)}
            slides={currentImage ? [{ src: currentImage }] : []}
        />
      </div>
  );
}

export default Providers;